import { useCallback, useRef } from 'react';
import type { TouchEvent } from 'react';
import { nextTab, startsInHorizontalScroller, swipeDirection } from './tabSwipe';

interface Start {
  x: number;
  y: number;
  at: number;
  /** Le geste est disqualifié dès son départ (zone qui défile, multi-doigts). */
  ignored: boolean;
}

/**
 * SC-972 — les deux gestionnaires à poser sur le corps de l'onglet. Rien n'est
 * rendu pendant le déplacement : on ne retient que le point de départ, et on
 * décide au relâchement.
 */
export function useTabSwipe<T>(tabs: readonly T[], current: T, onChange: (tab: T) => void) {
  const start = useRef<Start | null>(null);

  const onTouchStart = useCallback((event: TouchEvent<HTMLElement>) => {
    const touch = event.touches[0];
    if (!touch) {
      start.current = null;
      return;
    }
    start.current = {
      x: touch.clientX,
      y: touch.clientY,
      at: Date.now(),
      // Deux doigts, c'est un zoom ou un geste système ; et un geste né dans
      // une barre de pastilles appartient à la barre.
      ignored:
        event.touches.length > 1 ||
        startsInHorizontalScroller(event.target as Element | null, event.currentTarget),
    };
  }, []);

  const onTouchEnd = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      const from = start.current;
      start.current = null;
      if (!from || from.ignored) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const direction = swipeDirection(
        touch.clientX - from.x,
        touch.clientY - from.y,
        Date.now() - from.at,
      );
      const target = nextTab(tabs, current, direction);
      if (target !== null) onChange(target);
    },
    [tabs, current, onChange],
  );

  return { onTouchStart, onTouchEnd };
}
